import React from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { BLOG_POSTS } from "@/lib/blogPosts";
import {
  Wallet, ArrowUpRight, ArrowDownRight, Target, Repeat, FileDown,
  Lock, LayoutDashboard, ShieldCheck,
} from "lucide-react";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Tracker Daily Money",
  operatingSystem: "Web, Android",
  applicationCategory: "FinanceApplication",
  description:
    "A calm, private personal-finance ledger. Track money in, money out, and your running balance. Includes monthly budgets, per-category caps, recurring transactions, CSV import/export, and monthly PDF statements.",
  url: "https://trackerdaily.com",
  image: "https://trackerdaily.com/logo512.png",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", ratingCount: "1" },
};

export default function HomePage() {
  const { user, localMode, loading, continueAsGuest } = useAuth();
  const navigate = useNavigate();
  if (!loading && user && !localMode) return <Navigate to="/dashboard" replace />;

  const startGuest = () => {
    continueAsGuest();
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-brand-bg font-body text-brand-charcoal">
      <SEO
        title="Personal cash-flow tracker with monthly budgets"
        description="Tracker Daily Money is a calm, private personal-finance ledger. Track money in, money out, and your running balance. Monthly budgets, per-category caps, recurring transactions, CSV import/export, and monthly PDF statements. Free, no ads, no bank connection."
        path="/"
        jsonLd={jsonLd}
      />

      {/* Nav */}
      <header className="border-b border-brand-border bg-brand-surface/70 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="h-8 w-8 rounded-md bg-brand-primary flex items-center justify-center">
              <Wallet className="h-4 w-4 text-brand-bg" strokeWidth={1.75} />
            </div>
            <span className="font-display font-black text-xl tracking-tight">Tracker Daily</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="home-login-link">
              <Button variant="ghost" className="text-brand-charcoal hover:bg-brand-hover">Sign in</Button>
            </Link>
            <Link to="/register" data-testid="home-register-link">
              <Button className="bg-brand-primary text-brand-bg hover:bg-brand-primaryHover">Get started free</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-[11px] font-bold uppercase tracking-wider mb-4" data-testid="us-only-badge">
              <span className="text-sm leading-none">🇺🇸</span> Available in the United States
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-muted mb-4">
              Personal cash-flow tracker
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-charcoal">
              Every dollar,<br />
              <span className="text-brand-success">quietly tracked.</span>
            </h1>
            <p className="mt-6 text-lg text-brand-muted leading-relaxed max-w-xl">
              Tracker Daily Money is a calm, private personal-finance ledger. Log every dollar in and every
              dollar out. See your monthly balance in seconds — no bank connection, no ads, no data selling.
              Just an honest look at what came in and what went out.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-register">
                <Button className="bg-brand-primary text-brand-bg hover:bg-brand-primaryHover rounded-md px-6 py-6 text-base font-semibold">
                  Create your free ledger
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={startGuest}
                data-testid="hero-cta-guest"
                className="border-brand-border rounded-md px-6 py-6 text-base font-semibold"
              >
                Continue without account
              </Button>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-brand-muted">
              <ShieldCheck className="h-4 w-4 text-brand-success" strokeWidth={1.75} />
              Private by default · Free forever · Works offline (PWA)
            </div>
            <div className="mt-1 text-xs text-brand-muted">
              No account? Your data stays on this device. Sign up any time to sync across devices.
            </div>
          </div>

          {/* Preview card */}
          <div className="bg-brand-surface border border-brand-border rounded-2xl p-6 md:p-8 shadow-xl">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-muted mb-2">Total balance</div>
            <div className="font-display font-black text-5xl text-brand-charcoal">$3,957.49</div>
            <div className="text-xs text-brand-muted mt-1">3 transactions this month</div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="p-4 rounded-xl bg-brand-hover">
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">Money in</div>
                <div className="font-display font-black text-2xl text-brand-success mt-1">$3,999.99</div>
              </div>
              <div className="p-4 rounded-xl bg-brand-hover">
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">Money out</div>
                <div className="font-display font-black text-2xl text-brand-danger mt-1">$42.50</div>
              </div>
            </div>
            <div className="mt-6">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="font-semibold">Monthly cap</span>
                <span className="text-brand-muted">$1,200 of $2,000</span>
              </div>
              <div className="h-2 w-full bg-brand-hover rounded-full overflow-hidden">
                <div className="h-full bg-brand-success" style={{ width: "60%" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-brand-border">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-muted mb-4">What's inside</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl mb-12 max-w-2xl">
          Everything you need to understand your money — and nothing you don't.
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: LayoutDashboard, title: "Balance at a glance", copy: "Total balance, money in, money out — one dashboard, one clear picture. Filter by date range, category, or type." },
            { icon: Target, title: "Budgets that warn you early", copy: "Set a monthly spending cap plus per-category caps. Get a gentle nudge when you hit 80%, a firm one when you go over." },
            { icon: Repeat, title: "Recurring transactions", copy: "Salary, rent, subscriptions — auto-post on the day you choose. Set it once, never forget it." },
            { icon: FileDown, title: "CSV + monthly PDF", copy: "Import from your bank in seconds. Export CSV whenever you want. Download a beautiful monthly PDF statement." },
            { icon: ArrowUpRight, title: "Made for the US", copy: "USD-native, built for United States users. Simple, fast, and no confusing conversion rates." },
            { icon: Lock, title: "Private by default", copy: "Your data lives on your own account. No bank connection, no ads, no data selling." },
          ].map(({ icon: Icon, title, copy }) => (
            <article key={title} className="p-6 border border-brand-border rounded-xl bg-brand-surface hover:-translate-y-0.5 transition-transform">
              <div className="h-10 w-10 rounded-md bg-brand-primary/10 text-brand-primary flex items-center justify-center mb-4">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-display font-bold text-lg mb-2">{title}</h3>
              <p className="text-sm text-brand-muted leading-relaxed">{copy}</p>
            </article>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-brand-border">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-muted mb-4">How it works</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl mb-12 max-w-2xl">Three steps. Ten minutes. Lifetime clarity.</h2>
        <ol className="grid md:grid-cols-3 gap-8 list-none">
          {[
            { n: "01", title: "Create your ledger", copy: "Sign up with an email and password. No bank connection required." },
            { n: "02", title: "Log money in and out", copy: "Add transactions with category, note, and date. Or import last month's CSV from your bank." },
            { n: "03", title: "Watch your balance", copy: "Your total balance, monthly summary, and budget progress update instantly." },
          ].map(({ n, title, copy }) => (
            <li key={n} className="border-t-2 border-brand-primary pt-6">
              <div className="font-display font-black text-brand-primary text-4xl mb-4">{n}</div>
              <h3 className="font-display font-bold text-xl mb-2">{title}</h3>
              <p className="text-brand-muted leading-relaxed">{copy}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* From the blog */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-brand-border" data-testid="home-blog-section">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-muted mb-2">From the blog</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-brand-charcoal">Money habits that actually stick</h2>
          </div>
          <Link to="/blog" className="text-sm font-semibold text-brand-primary hover:underline" data-testid="home-blog-view-all">
            All articles →
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {BLOG_POSTS.slice(0, 2).map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`home-blog-card-${p.slug}`}
              className="block p-6 md:p-7 rounded-2xl border border-brand-border bg-brand-surface hover:-translate-y-0.5 hover:shadow-md transition-all group"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-2">{p.readingMinutes} min read</div>
              <h3 className="font-display font-bold text-xl text-brand-charcoal group-hover:text-brand-primary transition-colors mb-2">{p.title}</h3>
              <p className="text-sm text-brand-muted leading-relaxed">{p.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ (SEO-rich) */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 border-t border-brand-border">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-muted mb-4">FAQ</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl mb-8">Questions people ask</h2>
        <div className="space-y-6">
          {[
            {
              q: "Is Tracker Daily Money free?",
              a: "Yes. Creating an account and using every feature — money-in/out, budgets, recurring transactions, CSV import/export, monthly PDF statements — is free.",
            },
            {
              q: "Do you connect to my bank?",
              a: "No. Tracker Daily Money is a private ledger. You log transactions yourself or import a CSV from your bank. Your login credentials are never shared with any third party.",
            },
            {
              q: "Can I use Tracker Daily on my phone?",
              a: "Yes. Install it from Chrome or Safari as a Progressive Web App — it looks and feels like a native app. An Android version is coming to Google Play.",
            },
            {
              q: "Where is Tracker Daily available?",
              a: "Tracker Daily Money is currently available only to users in the United States. All amounts are in US dollars (USD).",
            },
            {
              q: "How do I export or back up my data?",
              a: "Every transaction is one click away from a CSV export or a beautifully formatted monthly PDF statement.",
            },
          ].map(({ q, a }) => (
            <div key={q}>
              <h3 className="font-display font-bold text-lg mb-1">{q}</h3>
              <p className="text-brand-muted leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-primary text-brand-bg py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display font-black text-3xl md:text-5xl leading-tight mb-6">
            Start tracking today.<br />See your money clearly by month's end.
          </h2>
          <p className="text-brand-bg/80 max-w-xl mx-auto mb-8">
            No credit card. No bank connection. Just a calm ledger for the money you already earn and spend.
          </p>
          <Link to="/register" data-testid="footer-cta-register">
            <Button className="bg-brand-bg text-brand-primary hover:bg-brand-hover rounded-md px-8 py-6 text-base font-semibold">
              Create your free account
            </Button>
          </Link>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 md:px-10 py-8 text-sm text-brand-muted flex flex-col md:flex-row items-center justify-between gap-3">
        <div>© Tracker Daily Money · trackerdaily.com</div>
        <div className="flex items-center gap-4">
          <Link to="/login" className="hover:text-brand-charcoal">Sign in</Link>
          <Link to="/register" className="hover:text-brand-charcoal">Create account</Link>
          <Link to="/forgot-password" className="hover:text-brand-charcoal">Forgot password</Link>
          <Link to="/privacy" className="hover:text-brand-charcoal" data-testid="footer-privacy-link">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
